import React from 'react';
import { Formik } from 'formik';
import { FormikInput as Input } from '@front-activerse/common-components';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Container,
  Row,
  Col,
  Button,
} from 'reactstrap';

interface TrackerMealNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  mealName: string;
  onSave: (values: { mealName?: string }) => void;
  t: (key: string) => string;
}

export const TrackerMealNameModal: React.FC<TrackerMealNameModalProps> = ({
  isOpen,
  onClose,
  mealName,
  onSave,
  t,
}) => (
  <Modal className="from-bottom" isOpen={isOpen} toggle={onClose} fullscreen size="lg">
    <div className="change-meal-name">
      <Container>
        <p className="small medium text-center">{t('tracker.logMeal.mealName.edit.title')}</p>
        <Row>
          <Col xs="12" md={{ size: 4, offset: 4 }}>
            <Formik initialValues={{ mealName }} onSubmit={() => undefined} enableReinitialize>
              {({ values }) => (
                <>
                  <Input
                    name="mealName"
                    label={t('tracker.logMeal.mealName.edit.inputLabel')}
                    type="text"
                    translateErrMsg
                  />
                  <Row>
                    <Col xs="6">
                      <Button color="link" className="w-100" onClick={onClose}>
                        {t('tracker.logMeal.edit.cancelButton')}
                      </Button>
                    </Col>
                    <Col xs="6">
                      <Button
                        color="primary"
                        className="w-100"
                        onClick={() => onSave(values as { mealName?: string })}
                      >
                        {t('tracker.logMeal.mealName.edit.saveButton')}
                      </Button>
                    </Col>
                  </Row>
                </>
              )}
            </Formik>
          </Col>
        </Row>
      </Container>
    </div>
  </Modal>
);

interface TrackerMealRemoveModalProps {
  isOpen: boolean;
  onToggle: () => void;
  onConfirm: () => void;
  t: (key: string) => string;
}

export const TrackerMealRemoveModal: React.FC<TrackerMealRemoveModalProps> = ({
  isOpen,
  onToggle,
  onConfirm,
  t,
}) => (
  <Modal isOpen={isOpen} toggle={onToggle} centered size="md">
    <ModalBody>
      <h4 className="title-medium semi-bold mb-l mt-s">
        {t('tracker.history.removePopup.appMeal.title')}
      </h4>
      <p className="mb-0 text-text-variant-color">
        {t('tracker.history.removePopup.appMeal.description')}
      </p>
    </ModalBody>
    <ModalFooter>
      <Row className="w-100 m-0">
        <Col xs="6" className="ps-0 pe-2">
          <Button color="secondary" outline className="w-100" onClick={onToggle}>
            {t('tracker.history.removePopup.cancelButton')}
          </Button>
        </Col>
        <Col xs="6" className="ps-2 pe-0">
          <Button color="primary" className="w-100" onClick={onConfirm} disabled={!isOpen}>
            {t('tracker.history.removePopup.removeButton')}
          </Button>
        </Col>
      </Row>
    </ModalFooter>
  </Modal>
);

interface TrackerMealAiModalProps {
  isOpen: boolean;
  onToggle: () => void;
  t: (key: string) => string;
  // Overrides the body copy for callers whose subject is not a meal — the add-product form talks
  // about a product, so it passes `tracker.aiSignature.product.popup.description`. Same pattern the
  // activity screen uses with its own `…popup.activity.description` key.
  descriptionKey?: string;
}

export const TrackerMealAiModal: React.FC<TrackerMealAiModalProps> = ({
  isOpen,
  onToggle,
  t,
  descriptionKey = 'tracker.aiSignature.popup.description',
}) => (
  <Modal isOpen={isOpen} toggle={onToggle} centered size="md">
    <ModalHeader toggle={onToggle} />
    <ModalBody className="pt-0">
      <h2 className="title-medium semi-bold">{t('tracker.aiSignature.popup.title')}</h2>
      <p>{t(descriptionKey)}</p>
      <Button color="primary" className="w-100" onClick={onToggle}>
        {t('tracker.aiSignature.popup.button')}
      </Button>
    </ModalBody>
  </Modal>
);
